<template>
  <div class="list-page-container">
    <!-- 顶部操作区 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-coin"></i>
          <span>{{ $t('resourceManagement.resourceQuotaTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button 
            type="primary" 
            @click="goToAllocate" 
            v-if="hasPermission('sys:resource:allocate')"
          >
            <i class="el-icon-plus"></i> {{ $t('resourceManagement.allocateQuota') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <!-- 搜索筛选区 -->
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input
              v-model="searchForm.keyword"
              :placeholder="$t('common.search')"
              class="filter-input"
              clearable
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.status"
              :placeholder="$t('common.status')"
              class="filter-select"
              clearable
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-info"></i>
              <el-option :label="$t('common.active')" value="active"></el-option>
              <el-option :label="$t('common.inactive')" value="inactive"></el-option>
            </el-select>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>

        <!-- 批量操作与工具区 -->
        <div class="action-wrapper">
          <div class="left-actions">
            <el-button-group class="batch-group">
              <el-button 
                type="primary" 
                @click="handleBatchAdjust" 
                plain
                size="small"
                v-if="hasPermission('sys:resource:update')"
              >
                <i class="el-icon-setting"></i> {{ $t('resourceManagement.batchAdjust') }}
              </el-button>
              <el-button 
                type="warning" 
                @click="handleBatchEdit" 
                :disabled="!selectedQuotas.length"
                plain
                size="small"
                v-if="hasPermission('sys:resource:update')"
              >
                <i class="el-icon-edit"></i> {{ $t('resourceManagement.batchEditQuota') }}
              </el-button>
              <el-button 
                type="danger" 
                @click="handleBatchDelete" 
                :disabled="!selectedQuotas.length"
                plain
                size="small"
                v-if="hasPermission('sys:resource:delete')"
              >
                <i class="el-icon-delete"></i> {{ $t('common.batchDelete') }}
              </el-button>
            </el-button-group>
          </div>
          <div class="right-tools">
            <el-button @click="fetchQuotaList" icon="el-icon-refresh" circle size="small" :title="$t('common.refresh')"></el-button>
            <el-button type="info" @click="handleExport" :loading="exporting" icon="el-icon-download" circle size="small" :title="$t('common.export')" v-if="hasPermission('sys:resource:export')"></el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 资源配额列表 -->
    <div class="main-content">
      <el-card class="list-card">
        <el-table
          :data="quotaList"
          v-loading="loading"
          height="100%"
          style="width: 100%"
          @selection-change="handleSelectionChange"
          class="custom-table"
        >
          <el-table-column type="selection" width="55" align="center"></el-table-column>

          <el-table-column 
            prop="userName" 
            :label="$t('common.username')" 
            min-width="120" 
            align="center"
          >
            <template slot-scope="scope">
              <el-button type="text" class="username-btn" @click="handleViewUser(scope.row.userId)">{{ scope.row.userName }}</el-button>
            </template>
          </el-table-column>

          <el-table-column 
            :label="$t('resourceManagement.resourceType')" 
            min-width="100" 
            align="center"
          >
            <template slot-scope="scope">
              <el-tag type="primary" size="mini">{{ $t('resourceManagement.token') }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column
            prop="allocatedTokens"
            :label="$t('resourceManagement.quotaValue')"
            min-width="120"
            align="center"
          >
            <template slot-scope="scope">
              {{ scope.row.allocatedTokens }}
            </template>
          </el-table-column>

          <el-table-column
            prop="usedTokens"
            :label="$t('resourceManagement.usedValue')"
            min-width="120"
            align="center"
          >
            <template slot-scope="scope">
              {{ scope.row.usedTokens }} {{ $t('resourceManagement.token') }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('resourceManagement.remainingValue')"
            min-width="120"
            align="center"
          >
            <template slot-scope="scope">
              <span :class="(scope.row.allocatedTokens - scope.row.usedTokens) < scope.row.allocatedTokens * (scope.row.threshold / 100) ? 'text-danger' : ''">
                {{ scope.row.allocatedTokens - scope.row.usedTokens }} {{ $t('resourceManagement.token') }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('resourceManagement.utilizationRate')"
            min-width="180"
            align="center"
          >
            <template slot-scope="scope">
              <el-progress
                :percentage="Math.round((scope.row.usedTokens / scope.row.allocatedTokens) * 100) || 0"
                :color="getUtilizationColor(Math.round((scope.row.usedTokens / scope.row.allocatedTokens) * 100) || 0)"
                :stroke-width="10"
              ></el-progress>
            </template>
          </el-table-column>

          <el-table-column
            prop="status"
            :label="$t('common.status')"
            width="100"
            align="center"
          >
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'" size="small">
                {{ scope.row.status === 'active' ? $t('common.active') : $t('common.inactive') }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column :label="$t('common.operation')" width="320" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-view"
                  @click="handleViewDetails(scope.row)"
                >
                  {{ $t('common.detail') }}
                </el-button>
                <el-button
                  v-if="hasPermission('sys:resource:update')"
                  size="mini"
                  type="success"
                  plain
                  icon="el-icon-edit"
                  @click="handleEditQuota(scope.row)"
                >
                  {{ $t('common.edit') }}
                </el-button>
                <el-button
                  v-if="hasPermission('sys:resource:update')"
                  size="mini"
                  type="warning"
                  plain
                  :icon="scope.row.status === 'active' ? 'el-icon-video-pause' : 'el-icon-video-play'"
                  @click="handleToggleStatus(scope.row)"
                >
                  {{ scope.row.status === 'active' ? $t('common.inactive') : $t('common.active') }}
                </el-button>
                <el-button
                  v-if="hasPermission('sys:resource:delete')"
                  type="danger"
                  size="mini"
                  plain
                  icon="el-icon-delete"
                  @click="handleDeleteQuota(scope.row)"
                >
                  {{ $t('common.delete') }}
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
          ></el-pagination>
        </div>
      </el-card>
    </div>

    <!-- 底部悬浮批量操作栏 -->
    <transition name="el-zoom-in-bottom">
      <div v-if="selectedQuotas.length > 0" class="batch-footer-bar">
        <div class="batch-info">
          <i class="el-icon-info"></i>
          <span>{{ $t('userManagement.selectedCount', { count: selectedQuotas.length }) }}</span>
        </div>
        <div class="batch-buttons">
          <el-button
            v-if="hasPermission('sys:resource:update')"
            type="primary"
            size="small"
            icon="el-icon-edit"
            @click="handleBatchEdit"
          >
            {{ $t('resourceManagement.batchEditQuota') }}
          </el-button>
          <el-button
            v-if="hasPermission('sys:resource:delete')"
            type="danger"
            size="small"
            icon="el-icon-delete"
            @click="handleBatchDelete"
            :loading="batchDeleting"
          >
            {{ $t('common.batchDelete') }}
          </el-button>
          <el-divider direction="vertical"></el-divider>
          <el-button type="text" @click="selectedQuotas = []" style="color: #94a3b8;">{{ $t('common.cancel') }}</el-button>
        </div>
      </div>
    </transition>

    <!-- 批量编辑对话框 -->
    <el-dialog
      :title="$t('resourceManagement.batchEditQuota')"
      :visible.sync="batchEditDialogVisible"
      width="600px"
      append-to-body
      custom-class="custom-dialog batch-edit-dialog"
    >
      <div class="dialog-content">
        <el-form :model="batchEditForm" :rules="batchEditRules" ref="batchEditForm" label-position="top" size="medium">
          <!-- 调整设置 -->
          <div class="adjust-section section-block">
            <div class="section-title"><i class="el-icon-setting"></i> {{ $t('resourceManagement.adjustSetting') }}</div>
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item :label="$t('resourceManagement.adjustMode')" prop="mode">
                  <el-select v-model="batchEditForm.mode" style="width: 100%">
                    <el-option :label="$t('resourceManagement.fixedAdd')" value="FIXED_ADD"></el-option>
                    <el-option :label="$t('resourceManagement.percentageAdd')" value="PERCENT_ADD"></el-option>
                    <el-option :label="$t('resourceManagement.fixedSub')" value="FIXED_SUB"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item :label="$t('resourceManagement.amount')" prop="amount">
                  <el-input-number v-model="batchEditForm.amount" :min="1" style="width: 100%" controls-position="right"></el-input-number>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item :label="$t('resourceManagement.validity')" prop="validity">
                  <el-radio-group v-model="batchEditForm.validity" size="small">
                    <el-radio-button label="PERMANENT">{{ $t('resourceManagement.permanent') }}</el-radio-button>
                    <el-radio-button label="TEMPORARY">{{ $t('resourceManagement.temporary') }}</el-radio-button>
                  </el-radio-group>
                </el-form-item>
              </el-col>
              <el-col :span="12" v-if="batchEditForm.validity === 'TEMPORARY'">
                <el-form-item :label="$t('resourceManagement.expiryTime')" prop="expiryTime">
                  <el-date-picker v-model="batchEditForm.expiryTime" type="datetime" style="width: 100%" :placeholder="$t('resourceManagement.expiryTimePlaceholder')"></el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item :label="$t('common.status')" prop="status">
                  <el-radio-group v-model="batchEditForm.status" size="small">
                    <el-radio-button label="">{{ $t('resourceManagement.leaveEmptyNoChange') }}</el-radio-button>
                    <el-radio-button label="active">{{ $t('common.active') }}</el-radio-button>
                    <el-radio-button label="inactive">{{ $t('common.inactive') }}</el-radio-button>
                  </el-radio-group>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item :label="$t('common.remark')" prop="remark">
              <el-input v-model="batchEditForm.remark" type="textarea" :rows="2" :placeholder="$t('resourceManagement.remarkPlaceholder')"></el-input>
            </el-form-item>
          </div>

          <!-- 预览结果 -->
          <div class="preview-section section-block" v-if="batchEditPreviewData">
            <div class="section-title"><i class="el-icon-view"></i> {{ $t('resourceManagement.batchPreview') }}</div>
            <div class="preview-stats">
              <div class="stat-item">
                <div class="stat-label">{{ $t('resourceManagement.affectedQuotas') }}</div>
                <div class="stat-value">{{ batchEditPreviewData.affectedQuotaCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">{{ $t('resourceManagement.totalChange') }}</div>
                <div class="stat-value" :class="batchEditPreviewData.totalChange >= 0 ? 'text-success' : 'text-danger'">
                  {{ batchEditPreviewData.totalChange >= 0 ? '+' : '' }}{{ batchEditPreviewData.totalChange }}
                </div>
              </div>
            </div>
          </div>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchEditDialogVisible = false" size="small">{{ $t('common.cancel') }}</el-button>
        <el-button type="info" @click="handleBatchEditPreview" :loading="batchEditPreviewLoading" size="small" icon="el-icon-view">
          {{ $t('resourceManagement.batchPreview') }}
        </el-button>
        <el-button type="primary" @click="submitBatchEdit" :loading="submitting" size="small">
          {{ $t('common.confirm') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 批量调整对话框 (按条件筛选) -->
    <el-dialog
      :title="$t('resourceManagement.batchAdjust')"
      :visible.sync="batchAdjustDialogVisible"
      width="700px"
      append-to-body
      custom-class="custom-dialog batch-adjust-dialog"
    >
      <div class="dialog-content">
        <el-form :model="batchAdjustForm" :rules="batchAdjustRules" ref="batchAdjustForm" label-position="top" size="medium">
          <!-- 筛选条件 -->
          <div class="filter-section section-block">
            <div class="section-title"><i class="el-icon-filter"></i> {{ $t('resourceManagement.filterCondition') }}</div>
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item :label="$t('resourceManagement.selectGroups')" prop="groupIds">
                  <el-select v-model="batchAdjustForm.groupIds" multiple collapse-tags style="width: 100%" :placeholder="$t('resourceManagement.allUsers')">
                    <el-option v-for="group in userGroups" :key="group.id" :label="group.name" :value="group.id"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item :label="$t('resourceManagement.regTimeRange')">
                  <el-date-picker
                    v-model="batchAdjustForm.regTimeRange"
                    type="daterange"
                    :range-separator="$t('common.to')"
                    :start-placeholder="$t('common.startDate')"
                    :end-placeholder="$t('common.endDate')"
                    style="width: 100%"
                    value-format="yyyy-MM-dd HH:mm:ss"
                  ></el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 调整设置 -->
          <div class="adjust-section section-block">
            <div class="section-title"><i class="el-icon-setting"></i> {{ $t('resourceManagement.adjustSetting') }}</div>
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item :label="$t('resourceManagement.adjustMode')" prop="mode">
                  <el-select v-model="batchAdjustForm.mode" style="width: 100%">
                    <el-option :label="$t('resourceManagement.fixedAdd')" value="FIXED_ADD"></el-option>
                    <el-option :label="$t('resourceManagement.percentageAdd')" value="PERCENT_ADD"></el-option>
                    <el-option :label="$t('resourceManagement.fixedSub')" value="FIXED_SUB"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item :label="$t('resourceManagement.amount')" prop="amount">
                  <el-input-number v-model="batchAdjustForm.amount" :min="1" style="width: 100%" controls-position="right"></el-input-number>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item :label="$t('resourceManagement.validity')" prop="validity">
                  <el-radio-group v-model="batchAdjustForm.validity" size="small">
                    <el-radio-button label="PERMANENT">{{ $t('resourceManagement.permanent') }}</el-radio-button>
                    <el-radio-button label="TEMPORARY">{{ $t('resourceManagement.temporary') }}</el-radio-button>
                  </el-radio-group>
                </el-form-item>
              </el-col>
              <el-col :span="12" v-if="batchAdjustForm.validity === 'TEMPORARY'">
                <el-form-item :label="$t('resourceManagement.expiryTime')" prop="expiryTime">
                  <el-date-picker v-model="batchAdjustForm.expiryTime" type="datetime" style="width: 100%" :placeholder="$t('resourceManagement.expiryTimePlaceholder')"></el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item :label="$t('common.remark')" prop="remark">
              <el-input v-model="batchAdjustForm.remark" type="textarea" :rows="2" :placeholder="$t('resourceManagement.remarkPlaceholder')"></el-input>
            </el-form-item>
          </div>

          <!-- 预览结果 -->
          <div class="preview-section section-block" v-if="previewData">
            <div class="section-title"><i class="el-icon-view"></i> {{ $t('resourceManagement.batchPreview') }}</div>
            <div class="preview-stats">
              <div class="stat-item">
                <div class="stat-label">{{ $t('resourceManagement.affectedUsers') }}</div>
                <div class="stat-value">{{ previewData.affectedUserCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">{{ $t('resourceManagement.affectedQuotas') }}</div>
                <div class="stat-value">{{ previewData.affectedQuotaCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">{{ $t('resourceManagement.totalChange') }}</div>
                <div class="stat-value" :class="previewData.totalChange >= 0 ? 'text-success' : 'text-danger'">
                  {{ previewData.totalChange >= 0 ? '+' : '' }}{{ previewData.totalChange }}
                </div>
              </div>
            </div>
          </div>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchAdjustDialogVisible = false" size="small">{{ $t('common.cancel') }}</el-button>
        <el-button type="info" @click="handlePreview" :loading="previewLoading" size="small" icon="el-icon-view">
          {{ $t('resourceManagement.batchPreview') }}
        </el-button>
        <el-button type="primary" @click="submitBatchAdjust" :loading="submitting" size="small">
          {{ $t('resourceManagement.executeAdjust') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAvailableModels } from '@/apis/resource/resource'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'ResourceQuota',
  data() {
    return {
      searchForm: {
        keyword: '',
        userId: '',
        status: ''
      },
      resetCycles: [
        { label: 'resourceManagement.none', value: 'NEVER' },
        { label: 'resourceManagement.daily', value: 'DAY' },
        { label: 'resourceManagement.weekly', value: 'WEEK' },
        { label: 'resourceManagement.monthly', value: 'MONTH' },
        { label: 'resourceManagement.yearly', value: 'YEAR' }
      ],
      submitting: false,
      tableLoading: false,
      batchDeleting: false,
      exporting: false,
      quotaList: [],
      userList: [],
      selectedQuotas: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      loading: false,
      batchEditDialogVisible: false,
      batchAdjustDialogVisible: false,
      previewLoading: false,
      previewData: null,
      batchEditForm: {
        mode: 'FIXED_ADD',
        amount: 1000,
        validity: 'PERMANENT',
        expiryTime: null,
        status: '',
        remark: ''
      },
      batchEditPreviewData: null,
      batchEditPreviewLoading: false,
      batchAdjustForm: {
        groupIds: [],
        regTimeRange: [],
        mode: 'FIXED_ADD',
        amount: 1000,
        validity: 'PERMANENT',
        expiryTime: null,
        remark: ''
      },
      batchAdjustRules: {
        mode: [{ required: true, trigger: 'change' }],
        amount: [{ required: true, trigger: 'blur' }],
        validity: [{ required: true, trigger: 'change' }],
        expiryTime: [
          { validator: (rule, value, callback) => {
            if (this.batchAdjustForm.validity === 'TEMPORARY' && !value) {
              callback(new Error(this.$t('resourceManagement.expiryTimePlaceholder')))
            } else {
              callback()
            }
          }, trigger: 'change' }
        ]
      },
      batchEditRules: {
        mode: [{ required: true, trigger: 'change' }],
        amount: [{ required: true, trigger: 'blur' }],
        validity: [{ required: true, trigger: 'change' }],
        expiryTime: [
          { validator: (rule, value, callback) => {
            if (this.batchEditForm.validity === 'TEMPORARY' && !value) {
              callback(new Error(this.$t('resourceManagement.expiryTimePlaceholder')))
            } else {
              callback()
            }
          }, trigger: 'change' }
        ],
        status: [
          { required: false, trigger: 'change' }
        ],
        remark: [
          { required: false, trigger: 'blur' },
          { max: 200, message: this.$t('common.remarkLength'), trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 从 Store 获取用户组数据，确保数据同步
    userGroups() {
      return this.$store.getters.allUserGroups || []
    }
  },
  created() {
    this.fetchQuotaList()
    this.fetchUserList()
    this.fetchUserGroups()
  },
  methods: {
    hasPermission,
    // fetchData别名，供全局mixin在activated时自动刷新
    fetchData() {
      this.fetchQuotaList()
    },
    fetchQuotaList() {
      this.loading = true
      this.$store.dispatch('resource/fetchResourceQuotas', {
        ...this.searchForm,
        page: this.pagination.page,
        limit: this.pagination.pageSize
      }).then(response => {
        if (response && response.list) {
          this.quotaList = response.list
          this.pagination.total = response.total
        } else {
          this.quotaList = []
          this.pagination.total = 0
        }
      }).catch(error => {
        console.error('Fetch quota list error:', error)
        this.$message.error(this.$t('common.loadFailed') + '：' + (error.msg || error.message))
      }).finally(() => {
        this.loading = false
      })
    },
    fetchUserList() {
      this.$store.dispatch('user/fetchUsers', { page: 1, pageSize: 100 }).then(response => {
        // 这里的 response 已经是处理过的数据或者原始响应
        // 检查 user.js store 中的 fetchUsers 返回值
        if (response && response.list) {
          this.userList = response.list
        } else if (Array.isArray(response)) {
          this.userList = response
        }
      }).catch(error => {
        this.$message.error(this.$t('common.loadFailed') + '：' + error.message)
      })
    },
    fetchUserGroups() {
      // 触发 Store 中的 fetchUserGroups 动作，结果会通过 computed 属性同步
      this.$store.dispatch('fetchUserGroups').catch(error => {
        console.error('获取用户组失败:', error)
      })
    },
    handleBatchAdjust() {
      this.batchAdjustDialogVisible = true
      this.previewData = null
      this.fetchUserGroups()
      if (this.$refs.batchAdjustForm) {
        this.$refs.batchAdjustForm.resetFields()
        // 手动重置预览数据和部分默认值
        this.batchAdjustForm.mode = 'FIXED_ADD'
        this.batchAdjustForm.amount = 1000
        this.batchAdjustForm.validity = 'PERMANENT'
      }
    },
    handlePreview() {
      this.$refs.batchAdjustForm.validate(valid => {
        if (valid) {
          this.previewLoading = true
          const params = {
            ...this.batchAdjustForm,
            regTimeStart: this.batchAdjustForm.regTimeRange?.[0],
            regTimeEnd: this.batchAdjustForm.regTimeRange?.[1]
          }
          this.$store.dispatch('resource/previewBatchUpdateQuotas', params)
            .then(res => {
              this.previewData = res
            })
            .catch(err => {
              this.$message.error(this.$t('resourceManagement.previewFailed') + ': ' + (err.msg || err.message))
            })
            .finally(() => {
              this.previewLoading = false
            })
        }
      })
    },
    submitBatchAdjust() {
      this.$refs.batchAdjustForm.validate(valid => {
        if (valid) {
          if (!this.previewData) {
            this.$confirm(this.$t('resourceManagement.noPreviewConfirm'), this.$t('common.tips'), {
              confirmButtonText: this.$t('resourceManagement.executeDirectly'),
              cancelButtonText: this.$t('resourceManagement.previewFirst'),
              type: 'warning'
            }).then(() => {
              this.doExecuteBatchAdjust()
            }).catch(() => {
              this.handlePreview()
            })
          } else {
            this.doExecuteBatchAdjust()
          }
        }
      })
    },
    doExecuteBatchAdjust() {
      this.submitting = true
      const params = {
        ...this.batchAdjustForm,
        regTimeStart: this.batchAdjustForm.regTimeRange?.[0],
        regTimeEnd: this.batchAdjustForm.regTimeRange?.[1],
        expiryTime: this.batchAdjustForm.expiryTime ? new Date(this.batchAdjustForm.expiryTime).getTime() : null
      }
      this.$store.dispatch('resource/executeBatchUpdateQuotas', params)
        .then(() => {
          this.$message.success(this.$t('resourceManagement.batchAdjustSuccess'))
          this.batchAdjustDialogVisible = false
          this.fetchQuotaList()
        })
        .catch(err => {
          this.$message.error(this.$t('resourceManagement.executeFailed') + ': ' + (err.msg || err.message))
        })
        .finally(() => {
          this.submitting = false
        })
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchQuotaList()
    },
    handleReset() {
      this.searchForm = {
        keyword: '',
        userId: '',
        status: ''
      }
      this.pagination.page = 1
      this.fetchQuotaList()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchQuotaList()
    },
    handleCurrentChange(current) {
      this.pagination.page = current
      this.fetchQuotaList()
    },
    handleSelectionChange(selection) {
      this.selectedQuotas = selection
    },
    goToAllocate() {
      this.$router.push({ name: 'QuotaAllocation' })
    },
    handleEditQuota(quota) {
      this.$router.push({
        name: 'QuotaAllocation',
        query: { id: quota.id }
      })
    },
    hasPermission(permission) {
      return hasPermission(permission)
    },
    getResourceTypeIcon(type) {
      return 'el-icon-coin'
    },
    getResourceTypeLabel(type) {
      return 'Token'
    },
    getResourceTypeColor(type) {
      return 'primary'
    },
    getResetCycleName(cycle) {
      const item = this.resetCycles.find(i => i.value === cycle)
      return item ? this.$t(item.label) : cycle
    },
    handleDeleteQuota(quota) {
      this.$confirm(
        this.$t('resourceManagement.deleteQuotaConfirm', { type: 'Token' }),
        this.$t('common.prompt'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        return this.$store.dispatch('resource/deleteResourceQuota', quota.id)
      }).then(() => {
        this.$message.success(this.$t('common.deleteSuccess'))
        this.fetchQuotaList()
      }).catch(error => {
        if (error !== 'cancel') {
          this.$message.error(this.$t('common.deleteFailed'))
        }
      })
    },
    handleBatchDelete() {
      this.$confirm(
        this.$t('common.batchDeleteConfirm', { count: this.selectedQuotas.length }),
        this.$t('common.prompt'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        const quotaIds = this.selectedQuotas.map(quota => quota.id)
        return this.$store.dispatch('resource/batchDeleteResourceQuotas', quotaIds)
      }).then(() => {
        this.$message.success(this.$t('common.batchDeleteSuccess'))
        this.selectedQuotas = []
        this.fetchQuotaList()
      }).catch(error => {
        if (error !== 'cancel') {
          this.$message.error(this.$t('common.batchDeleteFailed'))
        }
      })
    },
    handleBatchEdit() {
      if (this.selectedQuotas.length === 0) return
      this.batchEditForm = {
        mode: 'FIXED_ADD',
        amount: 1000,
        validity: 'PERMANENT',
        expiryTime: null,
        status: '',
        remark: ''
      }
      this.batchEditPreviewData = null
      this.batchEditDialogVisible = true
      this.$nextTick(() => {
        this.$refs.batchEditForm && this.$refs.batchEditForm.clearValidate()
      })
    },
    handleBatchEditPreview() {
      this.$refs.batchEditForm.validate(valid => {
        if (valid) {
          this.batchEditPreviewLoading = true
          const quotaIds = this.selectedQuotas.map(quota => quota.id)
          const params = {
            ids: quotaIds,
            ...this.batchEditForm
          }
          this.$store.dispatch('resource/previewBatchUpdateQuotas', params)
            .then(res => {
              this.batchEditPreviewData = res
            })
            .catch(err => {
              this.$message.error(this.$t('resourceManagement.previewFailed') + ': ' + (err.msg || err.message))
            })
            .finally(() => {
              this.batchEditPreviewLoading = false
            })
        }
      })
    },
    submitBatchEdit() {
      this.$refs.batchEditForm.validate(valid => {
        if (valid) {
          if (!this.batchEditPreviewData) {
            this.$confirm(this.$t('resourceManagement.noPreviewModifyConfirm'), this.$t('common.tips'), {
              confirmButtonText: this.$t('resourceManagement.executeDirectly'),
              cancelButtonText: this.$t('resourceManagement.previewFirst'),
              type: 'warning'
            }).then(() => {
              this.doExecuteBatchEdit()
            }).catch(() => {
              this.handleBatchEditPreview()
            })
          } else {
            this.doExecuteBatchEdit()
          }
        }
      })
    },
    doExecuteBatchEdit() {
      this.submitting = true
      const quotaIds = this.selectedQuotas.map(quota => quota.id)
      const params = {
        ids: quotaIds,
        ...this.batchEditForm,
        expiryTime: this.batchEditForm.expiryTime ? new Date(this.batchEditForm.expiryTime).getTime() : null
      }
      this.$store.dispatch('resource/executeBatchUpdateQuotas', params)
        .then(() => {
          this.$message.success(this.$t('resourceManagement.batchEditSuccess'))
          this.batchEditDialogVisible = false
          this.selectedQuotas = []
          this.fetchQuotaList()
        })
        .catch(err => {
          this.$message.error(this.$t('resourceManagement.executeFailed') + ': ' + (err.msg || err.message))
        })
        .finally(() => {
          this.submitting = false
        })
    },
    handleExport() {
      this.exporting = true
      this.$store.dispatch('resource/exportResourceQuotas', this.searchForm)
        .then(response => {
          // 处理文件下载
          const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `资源配额列表_${new Date().getTime()}.xlsx`
          link.click()
          window.URL.revokeObjectURL(link.href)
          this.$message.success(this.$t('common.exportSuccess'))
        })
        .catch(error => {
          this.$message.error(this.$t('common.exportFailed') + '：' + error.message)
        })
        .finally(() => {
          this.exporting = false
        })
    },
    handleViewUser(userId) {
      if (userId) {
        this.$router.push(`/user/detail/${userId}`)
      }
    },
    handleViewDetails(row) {
      this.$router.push({
        path: '/resource/usage',
        query: {
          id: row.id,
          userId: row.userId
        }
      })
    },
    handleViewLogs(row) {
      this.$router.push({
        path: '/resource/logs',
        query: {
          userId: row.userId,
          tab: 'op'
        }
      })
    },
    handleEditQuota(quota) {
      this.$router.push({
        name: 'QuotaAllocation',
        query: { id: quota.id }
      })
    },
    handleToggleStatus(quota) {
      const newStatus = quota.status === 'active' ? 'inactive' : 'active'
      this.$store.dispatch('resource/updateResourceQuotaStatus', { id: quota.id, status: newStatus })
        .then(() => {
          this.$message.success(this.$t('common.operationSuccess'))
          this.fetchQuotaList()
        })
        .catch(error => {
          this.$message.error(this.$t('common.operationFailed') + '：' + error.message)
        })
    },
    formatTooltip(val) {
      return val + '%'
    },
    getUtilizationColor(utilization) {
      if (utilization >= 80) return '#F56C6C'
      if (utilization >= 50) return '#E6A23C'
      return '#67C23A'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 资源配额页面特有样式 */
.list-page-container {
  .filter-form {
    .filter-input, .filter-select { width: 180px; }
  }

  .custom-table {
    .username-btn {
      font-weight: 600;
      color: #409eff;
      padding: 0;
      &:hover { text-decoration: underline; }
    }
  }

  .text-danger { color: #ef4444; font-weight: 600; }

  .custom-dialog {
    border-radius: 12px;
    .el-dialog__header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; .el-dialog__title { font-weight: 600; } }
    .el-dialog__body { padding: 24px; }
    .el-dialog__footer { padding: 16px 24px; border-top: 1px solid #f1f5f9; background-color: #f8fafc; }
    .dialog-content {
      .el-form-item {
        margin-bottom: 20px;
        .el-form-item__label { font-size: 13px; font-weight: 500; color: #64748b; padding-bottom: 8px; }
      }
      .el-input__inner, .el-textarea__inner { border-radius: 8px; border-color: #e2e8f0; }
      .el-radio-button__inner { border-radius: 6px !important; margin-right: 8px; border: 1px solid #e2e8f0 !important; }
    }
    .section-block { background-color: #f8fafc; border-radius: 10px; padding: 16px; margin-bottom: 20px; border: 1px solid #f1f5f9;
      .section-title { font-size: 14px; font-weight: 600; color: #334155; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; i { color: #409eff; } }
    }
    .preview-stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      .stat-item { background: #fff; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; text-align: center;
        .stat-label { font-size: 12px; color: #64748b; }
        .stat-value { font-size: 16px; font-weight: 700; &.text-success { color: #10b981; } &.text-danger { color: #ef4444; } }
      }
    }
  }
}
</style>
